import { useState } from "react";
import { useI18n } from "../../../hooks/useTranslations";
import { StoredColumnFilterValue } from "../../../types";
import { Option } from "../HeaderMenuOption";
import Numeric from "../Numeric";
import FilterModeSwitcher from "./FilterModeSwitcher";
import FilterOptionList from "./FilterOptionList";
import FilterRangeInputs from "./FilterRangeInputs";
import { ColumnFilterProps } from "./types";
import { toggleListOption, useDistinctValues } from "./useDistinctValues";

type Mode = "equals" | "range" | "empty";

const deriveMode = (selected: StoredColumnFilterValue): Mode => {
  if (selected.operator === "gte" || selected.operator === "lte" || selected.operator === "between") return "range";
  if (!selected.operator && selected.values.length === 1 && selected.values[0] === null) return "empty";
  return "equals";
};

const NumberColumnFilter = ({ fieldName, formatter, get, selected, onChange }: ColumnFilterProps) => {
  const i18n = useI18n();
  const [mode, setMode] = useState<Mode>(() => deriveMode(selected));
  const [rangeFrom, setRangeFrom] = useState<number | null>(
    selected.operator === "gte" || selected.operator === "between" ? ((selected.values[0] as number) ?? null) : null,
  );
  const [rangeTo, setRangeTo] = useState<number | null>(
    selected.operator === "lte"
      ? ((selected.values[0] as number) ?? null)
      : selected.operator === "between"
        ? ((selected.values[1] as number) ?? null)
        : null,
  );
  const { options } = useDistinctValues(get, fieldName, selected.values);

  const selectMode = (nextMode: Mode) => {
    setMode(nextMode);
    setRangeFrom(null);
    setRangeTo(null);

    onChange({ operator: undefined, values: nextMode === "empty" ? [null] : [] });
  };

  const commitRange = (from: number | null, to: number | null) => {
    if (from !== null && to !== null) {
      onChange({ operator: "between", values: [from, to] });
    } else if (from !== null) {
      onChange({ operator: "gte", values: [from] });
    } else if (to !== null) {
      onChange({ operator: "lte", values: [to] });
    } else {
      onChange({ operator: undefined, values: [] });
    }
  };

  const toggleEquals = (option: Option) =>
    onChange({ operator: undefined, values: toggleListOption(selected.values, option) });

  return (
    <>
      <FilterModeSwitcher
        modes={[
          { value: "equals", label: i18n.t.common.filterEquals },
          { value: "range", label: i18n.t.common.filterRange },
          { value: "empty", label: i18n.t.common.empty },
        ]}
        active={mode}
        onSelect={selectMode}
      />

      {mode === "equals" && <FilterOptionList options={options} formatter={formatter} onToggle={toggleEquals} />}

      {mode === "range" && (
        <FilterRangeInputs
          fromLabel={i18n.t.common.from}
          toLabel={i18n.t.common.to}
          from={
            <Numeric
              autoFocus
              value={rangeFrom ?? undefined}
              onBlur={(value) => {
                setRangeFrom(value);
                commitRange(value, rangeTo);
              }}
            />
          }
          to={
            <Numeric
              value={rangeTo ?? undefined}
              onBlur={(value) => {
                setRangeTo(value);
                commitRange(rangeFrom, value);
              }}
            />
          }
        />
      )}
    </>
  );
};

export default NumberColumnFilter;
